import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Lightbulb, Send, X, ChevronDown } from 'lucide-react';

const CATEGORIES = [
  'Entertainment',
  'Sports',
  'The Globe',
  'Living Things',
  'The Abyss',
  'Food & Drink',
  'Gaming'
];

const DeckIdeaForm = ({ onClose }) => {
  const [deckName, setDeckName] = useState('');
  const [category, setCategory] = useState('');
  const [rankedBy, setRankedBy] = useState('');
  const [submitterName, setSubmitterName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!deckName.trim() || !category || !rankedBy.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/deck-ideas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deck_name: deckName.trim(),
          category,
          ranked_by: rankedBy.trim(),
          submitter_name: submitterName.trim() || null
        })
      });

      if (!response.ok) throw new Error('Failed to submit');
      
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Card className="bg-green-900/40 border-green-500/50 p-6 text-center">
        <div className="text-4xl mb-3">🎉</div>
        <h3 className="text-xl font-bold text-white mb-2">Thanks for your idea!</h3>
        <p className="text-gray-300 mb-4">We'll review it and may add it to the game.</p>
        <Button onClick={onClose} className="bg-green-600 hover:bg-green-700 text-white">
          Close
        </Button>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-800/90 border-purple-500/50 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-yellow-400" />
          <h3 className="text-lg font-bold text-white">Submit a Deck Idea</h3>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm text-gray-300 mb-1">Deck Name *</label>
          <Input
            type="text"
            value={deckName}
            onChange={(e) => setDeckName(e.target.value)}
            placeholder="e.g., Fast Food Items, Disney Movies"
            className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
            maxLength={50}
          />
        </div>

        <div>
          <label className="block text-sm text-gray-300 mb-1">Category *</label>
          <div className="relative">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-white/10 border border-white/20 text-white rounded-md px-3 py-2 appearance-none cursor-pointer"
            >
              <option value="" className="bg-slate-800">Select a category...</option>
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat} className="bg-slate-800">{cat}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-300 mb-1">Ranked By *</label>
          <Input
            type="text"
            value={rankedBy}
            onChange={(e) => setRankedBy(e.target.value)}
            placeholder="e.g., calories, box office sales, popularity"
            className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
            maxLength={100}
          />
        </div>

        <div>
          <label className="block text-sm text-gray-300 mb-1">Your Name (optional)</label>
          <Input
            type="text"
            value={submitterName}
            onChange={(e) => setSubmitterName(e.target.value)}
            placeholder="Anonymous"
            className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
            maxLength={30}
          />
        </div>

        {error && (
          <p className="text-red-400 text-sm">{error}</p>
        )}

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
        >
          {isSubmitting ? (
            'Submitting...'
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Submit Idea
            </>
          )}
        </Button>
      </form>
    </Card>
  );
};

export default DeckIdeaForm;
